import { telephonyApi } from './api'

/**
 * QUALIFICATION EN ARRIÈRE-PLAN — la file d'attente de l'AVANCÉE OPTIMISTE.
 *
 * ══════════════════════════════════════════════════════════════════════════
 * LE CONSTAT (priorité transversale ratifiée le 2026-07-30 : LA FLUIDITÉ)
 * ══════════════════════════════════════════════════════════════════════════
 *
 * Jusqu'ici, « Enregistrer » attendait le réseau : la modale restait ouverte
 * pendant le `POST /telephony/calls/:id/outcome`, la machine ne repassait au
 * repos qu'à la réponse, et l'enchaînement ne repartait qu'ensuite. L'agent
 * payait donc un aller-retour serveur ENTRE CHAQUE APPEL — sur un iPad en 4G,
 * c'est la moitié de la latence perçue de l'enchaînement, et elle tombe au
 * moment précis où il n'a rien d'autre à faire qu'attendre.
 *
 * Or le contact suivant est DÉJÀ chargé et DÉJÀ verrouillé au nom de l'agent
 * (`useNextContact`, depuis #45) : il n'y a rien à demander au serveur pour
 * l'afficher. La seule chose qui bloquait était l'enregistrement du résultat
 * PRÉCÉDENT — c'est-à-dire une écriture qui n'intéresse plus l'agent.
 *
 * ══════════════════════════════════════════════════════════════════════════
 * LA RÈGLE
 * ══════════════════════════════════════════════════════════════════════════
 *
 * Le clic « Enregistrer » (formulaire complet OU panneau d'échec) libère la
 * machine IMMÉDIATEMENT et dépose ici le résultat. Cette file s'en occupe :
 *
 *  1. **UNE SEULE ÉCRITURE EN VOL, JAMAIS DEUX.** `inFlight` est le verrou.
 *     Deux `POST /outcome` simultanés sur deux `Call` différents ne sont pas
 *     dangereux en soi, mais ils rendent l'ordre d'arrivée — donc l'ordre des
 *     `updateAfterCall()`, donc l'avancement de la campagne — non déterministe.
 *     Une file sérialise, et une file d'un seul élément à la fois se raisonne.
 *  2. **UN ÉCHEC N'INTERROMPT RIEN.** Le travail en échec passe en `failed` et
 *     y RESTE : il n'est rejoué que sur un geste explicite de l'agent
 *     (« Réessayer »). Rejouer tout seul sur un réseau coupé produirait une
 *     rafale de requêtes pendant l'appel suivant, exactement ce que la règle
 *     « jamais de scrutation » proscrit.
 *  3. **UN ÉCHEC NE BLOQUE PAS LES SUIVANTS.** Le travailleur ne prend que les
 *     travaux `queued` : une qualification en échec ne retient donc pas celle
 *     de l'appel d'après. Sans cette règle, un seul échec réseau au début d'une
 *     session gelait toutes les qualifications de la journée.
 *  4. **L'IDEMPOTENCE VIENT DU SERVEUR.** `setOutcome` est déjà idempotent
 *     (`firstQualification.count === 1` garde `updateAfterCall()`), ce qui rend
 *     un rejeu sans danger même si la requête d'origine avait en réalité
 *     abouti. C'est ce qui autorise « Réessayer » sans réconciliation.
 *
 * ⚠️ **CE QUE CETTE FILE OBLIGE AILLEURS.** Tant qu'une qualification n'est pas
 * enregistrée, `updateAfterCall()` n'a pas tourné : la ligne de campagne reste
 * ÉLIGIBLE en base. La sélection du contact suivant doit donc l'écarter — c'est
 * `pendingCampaignContactIds()`, consommé par `useCampaignDialer`. Sans cela,
 * l'agent se verrait re-servir la personne qu'il vient de qualifier : le bug
 * des doublons de la recette du 2026-07-29, par une autre porte.
 */

/** Ce qu'il faut pour rejouer une qualification, et rien de plus. */
export interface OutcomeJobInput {
  callId: string
  outcome: string
  notes?: string
  scheduledCallbackTime?: string
  /**
   * Ligne de campagne qualifiée — `null` pour un appel manuel ou entrant.
   * Sert UNIQUEMENT à l'exclure de la sélection tant que l'écriture n'a pas
   * abouti (cf. l'avertissement ci-dessus).
   */
  campaignContactId: string | null
  /** Nom (ou numéro) montré à l'agent si l'enregistrement échoue. */
  label: string
  /**
   * LOT-SCÈNE-LINK : contact créé (ou repris) dans le modal de qualification —
   * transporté jusqu'au POST /outcome pour que le serveur rattache la fiche à
   * l'appel. Absent = corps de requête historique, à l'octet près.
   */
  contactId?: string
  /**
   * POSTE-PROMESSE (#353) : montant promis (« Promesse de don » = INTERESTED +
   * montant), transporté jusqu'au POST /outcome. Absent = corps historique.
   */
  pledgedAmount?: number
}

export type OutcomeJobStatus = 'queued' | 'sending' | 'failed'

export interface OutcomeJob extends OutcomeJobInput {
  status: OutcomeJobStatus
  /** Nombre de tentatives déjà faites — affiché nulle part, utile au journal. */
  attempts: number
}

export interface OutcomeQueueState {
  jobs: readonly OutcomeJob[]
  /** Au moins un travail en échec : le bandeau « Réessayer » doit s'afficher. */
  hasFailed: boolean
}

type Sender = (
  callId: string,
  params: {
    outcome: string
    notes?: string
    scheduledCallbackTime?: string
    contactId?: string
    pledgedAmount?: number
  },
) => Promise<unknown>

/** Renonciation : le serveur clôt l'outcome et libère l'agent (cf. `discardFailed`). */
type Abandoner = (callId: string) => Promise<unknown>

const defaultSender: Sender = (callId, params) =>
  telephonyApi.setOutcome(callId, params)

const defaultAbandoner: Abandoner = (callId) => telephonyApi.abandonWrapup(callId)

let sender: Sender = defaultSender
let abandoner: Abandoner = defaultAbandoner
let jobs: OutcomeJob[] = []
let inFlight = false
let state: OutcomeQueueState = { jobs: [], hasFailed: false }
/** Agent dont la file est en mémoire — `null` tant que rien n'a été hydraté. */
let ownerId: string | null = null

type Listener = () => void
const listeners = new Set<Listener>()

/**
 * ══════════════════════════════════════════════════════════════════════════
 * PERSISTANCE — une qualification saisie ne se perd pas sur un rechargement
 * ══════════════════════════════════════════════════════════════════════════
 *
 * Mineur c de la review de #47. Sans elle, fermer l'onglet ou recharger la PWA
 * pendant qu'une écriture est en vol (ou en échec) perdait le résultat
 * **silencieusement** : l'agent avait saisi, l'écran avait avancé, et rien
 * n'était en base.
 *
 * `sessionStorage`, et pas `localStorage` : la portée est celle de l'onglet,
 * donc la file meurt avec la session de travail au lieu de ressusciter des
 * qualifications d'avant-hier. La clé porte l'identifiant de l'AGENT — un
 * changement d'utilisateur dans le même onglet ne doit ni rejouer ni perdre le
 * travail de l'autre.
 *
 * Le rejeu est sûr : `setOutcome` est idempotent côté serveur
 * (`firstQualification.count === 1` garde `updateAfterCall()`), donc une
 * requête qui avait en réalité abouti ne fait pas avancer la campagne deux fois.
 */
const STORAGE_PREFIX = 'telephony_v2_outcomes'

function storageKey(userId: string): string {
  return `${STORAGE_PREFIX}:${userId}`
}

function persist(): void {
  if (!ownerId) return
  try {
    if (jobs.length === 0) {
      sessionStorage.removeItem(storageKey(ownerId))
      return
    }
    // Rien n'est écrit comme « en vol » : au rechargement, l'onglet qui portait
    // la requête n'existe plus. Tout repart en `queued`.
    const persisted = jobs.map((job) => ({ ...job, status: 'queued' as const }))
    sessionStorage.setItem(storageKey(ownerId), JSON.stringify(persisted))
  } catch (error) {
    // Mode privé, quota : la persistance est un filet, jamais un point de
    // rupture. La file en mémoire continue de fonctionner.
    console.warn('[telephony-v2] File de qualifications non persistée', error)
  }
}

function publish(): void {
  jobs = [...jobs]
  state = {
    jobs,
    hasFailed: jobs.some((job) => job.status === 'failed'),
  }
  persist()
  // Copie : un abonné qui se désabonne pendant la diffusion ne casse pas
  // l'itération (même précaution que `callMachine` et `device.ts`).
  Array.from(listeners).forEach((listener) => {
    try {
      listener()
    } catch (error) {
      console.error('[telephony-v2] Abonné outcomeQueue en erreur', error)
    }
  })
}

async function pump(): Promise<void> {
  if (inFlight) return
  const job = jobs.find((entry) => entry.status === 'queued')
  if (!job) return

  inFlight = true
  job.status = 'sending'
  job.attempts += 1
  publish()

  try {
    await sender(job.callId, {
      outcome: job.outcome,
      notes: job.notes,
      scheduledCallbackTime: job.scheduledCallbackTime,
      // Clé OMISE (pas `undefined`) quand le travail n'en porte pas : le corps
      // du POST historique reste inchangé pour toute qualification sans fiche.
      ...(job.contactId ? { contactId: job.contactId } : {}),
      // POSTE-PROMESSE : même règle — la clé n'existe que pour une promesse chiffrée.
      ...(job.pledgedAmount !== undefined ? { pledgedAmount: job.pledgedAmount } : {}),
    })
    jobs = jobs.filter((entry) => entry !== job)
  } catch (error) {
    console.error('[telephony-v2] Qualification non enregistrée', {
      callId: job.callId,
      outcome: job.outcome,
      attempts: job.attempts,
      error,
    })
    job.status = 'failed'
  } finally {
    inFlight = false
    publish()
  }

  // Un travail vient de quitter l'état `queued` (envoyé ou mis en échec) : la
  // récursion est donc bornée par le nombre de travaux, elle ne peut pas
  // tourner sur un échec permanent.
  await pump()
}

export const outcomeQueue = {
  // --- Store externe (useSyncExternalStore) ---
  subscribe(listener: Listener): () => void {
    listeners.add(listener)
    return () => {
      listeners.delete(listener)
    }
  },
  getSnapshot(): OutcomeQueueState {
    return state
  },

  /**
   * Dépose une qualification. Rend la main TOUT DE SUITE — c'est là toute la
   * fluidité : l'appelant n'attend rien, l'écran avance.
   *
   * Un `callId` déjà présent est IGNORÉ : il n'y a qu'une qualification par
   * appel, et un double dépôt (double tap, effet rejoué) ferait deux écritures
   * pour un seul geste.
   */
  enqueue(input: OutcomeJobInput): void {
    if (jobs.some((job) => job.callId === input.callId)) {
      console.warn('[telephony-v2] Qualification déjà en file — dépôt ignoré', {
        callId: input.callId,
      })
      return
    }
    jobs.push({ ...input, status: 'queued', attempts: 0 })
    publish()
    void pump()
  },

  /** L'agent demande le rejeu des qualifications en échec. */
  retryFailed(): void {
    let changed = false
    jobs.forEach((job) => {
      if (job.status === 'failed') {
        job.status = 'queued'
        changed = true
      }
    })
    if (!changed) return
    publish()
    void pump()
  },

  /**
   * L'agent renonce à faire enregistrer ce résultat.
   *
   * ⚠️ LE SERVEUR EST PRÉVENU, et ce n'est pas un détail. C'est exactement ce
   * que faisait le bouton « Ignorer » du wrapup (#42) : sans lui, le `Call`
   * reste `ANSWERED` + `PENDING` en base, l'agent demeure exclu du routage
   * entrant pendant toute la fenêtre de wrapup, et la file d'attente n'est pas
   * relancée. L'avancée optimiste a déplacé le geste — elle ne doit pas
   * supprimer sa moitié serveur.
   *
   * « Au mieux » : une requête perdue laisse le nettoyage et la fenêtre
   * d'exclusion faire leur travail, comme avant.
   */
  discardFailed(): void {
    const abandoned = jobs.filter((job) => job.status === 'failed')
    if (abandoned.length === 0) return
    abandoned.forEach((job) => {
      console.warn('[telephony-v2] Qualification abandonnée par l’agent', {
        callId: job.callId,
        outcome: job.outcome,
      })
      void abandoner(job.callId).catch((error) => {
        console.warn('[telephony-v2] Abandon non transmis au serveur', error)
      })
    })
    jobs = jobs.filter((job) => job.status !== 'failed')
    publish()
  },

  /**
   * Lignes de campagne dont la qualification n'est pas encore écrite en base.
   * La sélection du contact suivant DOIT les écarter (cf. en-tête).
   */
  pendingCampaignContactIds(): string[] {
    return jobs
      .map((job) => job.campaignContactId)
      .filter((id): id is string => Boolean(id))
  },

  /**
   * REPREND les qualifications laissées par une session précédente de CET
   * agent (rechargement de la PWA, onglet fermé pendant une écriture).
   *
   * Appelée par le provider, qui seul connaît l'utilisateur. Idempotente :
   * ré-hydrater le même agent ne duplique rien (`enqueue` refuse un `callId`
   * déjà présent).
   */
  hydrate(userId: string): void {
    if (ownerId === userId) return
    ownerId = userId
    let restored: OutcomeJob[] = []
    try {
      const raw = sessionStorage.getItem(storageKey(userId))
      if (raw) restored = JSON.parse(raw) as OutcomeJob[]
    } catch (error) {
      console.warn('[telephony-v2] File de qualifications illisible', error)
      return
    }
    const pending = restored.filter((job) => job?.callId && job?.outcome)
    if (pending.length === 0) return
    console.warn(
      `[telephony-v2] ${pending.length} qualification(s) reprise(s) de la session précédente`,
    )
    pending.forEach((job) => {
      if (jobs.some((existing) => existing.callId === job.callId)) return
      jobs.push({ ...job, status: 'queued' })
    })
    publish()
    void pump()
  },

  /**
   * L'agent se déconnecte (ou change) : la file en MÉMOIRE est vidée, mais ce
   * qui est PERSISTÉ reste — c'est justement ce qui permettra de le reprendre
   * s'il revient dans cet onglet. Rien n'est perdu, rien ne fuit vers l'autre.
   */
  detach(): void {
    ownerId = null
    jobs = []
    inFlight = false
    publish()
  },

  /** Un travail est en vol — sert aux tests d'ordre, jamais à l'UI. */
  isSending(): boolean {
    return inFlight
  },

  /** Bancs d'essai uniquement : remplace les deux sorties réseau et vide la file. */
  __setSenderForTests(next: Sender | null, nextAbandoner: Abandoner | null = null): void {
    sender = next ?? defaultSender
    abandoner = nextAbandoner ?? (next ? async () => undefined : defaultAbandoner)
    jobs = []
    inFlight = false
    ownerId = null
    publish()
  },
}
